<template>
  <div class="opc-cfgcore">
    <config-plans
      :filter-keys="draftPlanFilterKeys"
      @remove="removePlanByKey"
      @add="addon"
      :configPlanTileStatus="configPlanTileStatus"
    />
    <partner-plan-card />
    <edu-tips v-if="showEduTips" />
    <template v-if="!isCartBuilderAddQty">
    <addon-cards :group="ADDON_GROUP.BIZ_PLUS" v-if="addonable || isShowBizCard" :filter-keys="addonFilterKeys" @add="addon">
      <div class="opc-cfgcore__addon">
        <h2 class="opc-cfgcore__addon__title">{{ $t('billing.zobizplus.promo_biz_title') }}</h2>
        <span v-if="!unlockBizLicenseCount" class="opc-cfgcore__addon__desc">{{ $t('billing.zobizplus.promo_biz_desc') }}</span>
      </div>
    </addon-cards>
    <addon-cards :group="ADDON_GROUP.JOINT_SALES" v-if="addonable" :filter-keys="addonFilterKeys" @add="addon">
      <div class="opc-cfgcore__exclusive_addon">
        <div class="opc-cfgcore__exclusive_addon__title-row">
          <h2 class="opc-cfgcore__exclusive_addon__title text-capitalize">{{ $t('billing.partner.special_promo.title') }}</h2>
          <span class="opc-cfgcore__exclusive_addon__subtitle">{{ $t('billing.partner.special_promo.limit_time') }}</span>
        </div>
      </div>
    </addon-cards>
    <addon-cards v-if="addonable" :filter-keys="addonFilterKeys" @add="addon">
      <div class="opc-cfgcore__addon">
        <h2 class="opc-cfgcore__addon__title text-capitalize">{{ subTitle }}</h2>
      </div>
    </addon-cards>
    </template>
  </div>
</template>

<script>
import { planTypes, ADDON_GROUP, unlockBizLicenseCount, BASE_PLAN_SUB_TYPES } from '@/common/constants';
import { AddOrRemoveBasePlanActionTypes } from '@/components/capacity/constants';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import ConfigPlans from './plans/index.js';
import AddonCards, { filterAddonForKeys } from './addon-cards';
import plans from '@/plans';
import { mapProducts } from '@/aspects/trackings';
import { getBasePlansFrom } from '@/common/product.js';
import log from '@/common/log';
import { isBizPlusContext, isShowBizCard } from '@/plans/ZOOM_MEETING/common/utils';
import { isCartBuilderAddQty } from '@/utils/cart-builder';
import EduTips from './EduTips.vue'
import PartnerPlanCard from '@/views/config/partner-refer/plans/config.vue';
import { isInUnlockBizPlusCountRetentionFlow } from '@/plans/ZOOM_ONE_BUNDLE/utils.js';

const CMR_PLAN_KEY = planTypes[planTypes.CMR]

export default {
  props: {
    dividerText: String,
    addonable: {
      type: Boolean,
      default: true,
    },
    addonFilter: {
      type: Function,
      default: () => true,
    }
  },
  components: {
    PartnerPlanCard,
    ConfigPlans,
    AddonCards,
    EduTips,
  },
  data() {
    return {
      isCartBuilderAddQty,
      ADDON_GROUP,
      // config plan tile expand or collapse status
      configPlanTileStatus: {},
      isBizPlusContext,
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'fromCampaignLink',
      'basicConfig',
      'isDisableAdd2Cart',
      'cmrPaywallTipVisible',
      'draftPlanFilterKeys',
      'boughtPlans',
      'freeTrialPlans'
    ]),
    ...mapMutations(['addOrRemoveBasePlan']),
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
      addOrRemoveBasePlanState: state => state.Product.addOrRemoveBasePlanState,
    }),
    showEduTips() {
      return !!this.getSelectedPlans.find(sp => sp.type === planTypes.ZOOM_MEETING && sp.category === BASE_PLAN_SUB_TYPES.EDUCATION)
    },
    selectedBasePlanType() {
      return getBasePlansFrom(this.getSelectedPlans)?.[0]?.type;
    },
    selectedTypes() {
      return [...new Set(this.getSelectedPlans.map((x) => x.type))];
    },
    planKeys() {
      // each plan that has config & is allowed in plan list of the currency
      const keys = plans
        .filter((x) => !!x?.components?.config)
        .map((x) => x.key)
        .filter((key) => !!this.planMapFlat.find((x) => x.type === planTypes[key]));
      return keys;
    },
    addonFilterForKeys() {
      return planTypes.map([this.selectedBasePlanType]);
    },
    allAddonPlans() {
      return plans?.filter((p) => this.planKeys?.includes(p?.key) && p.components?.addon) || [];
    },
    applicableAddonList() {
      if (this.isDisableAdd2Cart) {
        return [];
      }
      return filterAddonForKeys(
        this.allAddonPlans,
        this.addonFilterForKeys,
        this.getSelectedPlans,
        this.boughtPlans,
        this.freeTrialPlans
      );
    },
    rawAddonFilterKeys() {
      return this.applicableAddonList.filter((p) => {
        return !this.draftPlanFilterKeys.includes(p.key)
      }).map((x) => x.key).filter(this.addonFilter);
    },
    addonFilterKeys() {
      const keys = [...this.rawAddonFilterKeys]

      // Move CMR to the first
      let cmrPlanIndex = -1
      if (this.cmrPaywallTipVisible && (cmrPlanIndex = keys.indexOf(CMR_PLAN_KEY)) > 0) {
        keys.splice(cmrPlanIndex, 1)
        keys.unshift(CMR_PLAN_KEY)
      }

      return keys
    },
    subTitle() {
      return this.dividerText || this.$t('billing.enhance_experience');
    },
    isShowBizCard() {
      if (this.$context.is.add2cart) return false;
      if (this.basicConfig.isBulkLicenseAccount) return false;
      if (this.$context.is.pro2bundle) return false;
      if (isInUnlockBizPlusCountRetentionFlow()) return false;
      return isBizPlusContext && isShowBizCard();
    },
    unlockBizLicenseCount() {
      return unlockBizLicenseCount;
    },
    // Has CMR addon or has added CMR
    hasCmrAddonOrAddedCmr() {
      return this.rawAddonFilterKeys.includes(CMR_PLAN_KEY) || this.draftPlanFilterKeys.includes(CMR_PLAN_KEY)
    },
  },
  watch: {
    draftPlanFilterKeys: {
      immediate: true,
      deep: true,
      handler(newFilterKeys = [], oldFiltersKeys = []) {
        log('config:plans', newFilterKeys);
        newFilterKeys = this.filterSpecialKeys(newFilterKeys);
        oldFiltersKeys = this.filterSpecialKeys(oldFiltersKeys);
        // add config plan panel
        if (newFilterKeys?.length > oldFiltersKeys?.length) {
          // reset config plan expand status
          this.configPlanTileStatus = {};
          newFilterKeys?.forEach((key) => {
            this.$set(this.configPlanTileStatus, key, !!this.selectedTypes.includes(planTypes[key]));
          });
        }
      },
    },
    addOrRemoveBasePlanState: {
      immediate: true,
      handler(val) {
        if(val?.planType && [AddOrRemoveBasePlanActionTypes.ADD, AddOrRemoveBasePlanActionTypes.ADD_PAID_BASE_PLAN_FOR_FREE_TRIAL_BASE_PLAN].includes(val?.actionType) && !this.draftPlanFilterKeys.includes(val?.planType)) {
          this.addon(val?.planType);
        }
        if(val?.planType && [AddOrRemoveBasePlanActionTypes.REMOVE, AddOrRemoveBasePlanActionTypes.REMOVE_PAID_BASE_PLAN_FOR_FREE_TRIAL_BASE_PLAN].includes(val?.actionType) && this.draftPlanFilterKeys.includes(val?.planType)) {
          this.removePlanByKey(val?.planType);
        }
      },
    },
    hasCmrAddonOrAddedCmr: {
      immediate: true,
      handler(data) {
        this.setHasCmrAddonOrAddedCmr(data)
      },
    },
  },
  provide() {
    return {
      $core: this,
    };
  },
  methods: {
    ...mapMutations(['removePlan', 'setHasCmrAddonOrAddedCmr', 'setDraftPlanFilterKeys']),
    ...mapActions(['removePlanByKey']),
    addon(key) {
      // Add an empty draft plan
      const draftPlans = this.draftPlanFilterKeys.filter((k) => {
        return this.selectedTypes.includes(planTypes[k]);
      });
      if(draftPlans.includes(key)) return;
      this.setDraftPlanFilterKeys([...draftPlans, key]);
      this.scrollIntoView();
    },
    scrollIntoView() {
      setTimeout(() => {
        const dom = document.querySelector('.opc-config .opc-tile:last-child');
        if (!dom) return;
        dom.style['scroll-margin-top'] = '140px';
        dom?.scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    },
    removePlanByCode(key, plan) {
      this.removePlan(plan?.plan_code);
      // when the shopping cart remove all category plans, should remove this panel
      const isAddon = this.allAddonPlans?.some((p) => p.key === key);
      const currentSelectedPlans = this.getSelectedPlans.filter((plan) => plan.type === planTypes[key]);

      if (isAddon && currentSelectedPlans?.length === 0) {
        this.removeDraftPlanFilterKeysByKey(key);
      }
    },
    removeDraftPlanFilterKeysByKey(key) {
      const draftPlanFilterKeys = [...this.draftPlanFilterKeys];
      draftPlanFilterKeys.splice(
        this.draftPlanFilterKeys.findIndex((x) => x === key),
        1,
      );
      this.setDraftPlanFilterKeys(draftPlanFilterKeys);
    },
    filterSpecialKeys(keys) {
      return keys.filter(k => k !== planTypes.EVENT_SERVICES && k !== planTypes[planTypes.EVENT_SERVICES]);
    }
  },
  created() {
    let productKeys = this.$context.is.modal ?
      [...this.draftPlanFilterKeys] :
      [...this.draftPlanFilterKeys, ...this.addonFilterKeys]
    this.$tracking(() => ({
      event: 'Product View',
      products: mapProducts(
        productKeys.reduce((acc, key) => {
          const plans = this.planMapFlat.filter((plan) => plan.type === planTypes[key]);
          acc = [...acc, ...plans];
          return acc;
        }, []),
      ),
    }));
  },
};
</script>

<style lang="scss" scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils.scss';

.opc-cfgcore {
  &__addon, &__exclusive_addon {
    margin-top: 48px;
    padding-top: 48px;
    border-top: 2px solid #dfe3e8;
    @include darkTheme() {
      border-top: 2px solid #313235;
    }
  }

  &__addon {
    margin-bottom: 24px;
    &__title {
      font-weight: 700;
      font-size: 22px;
      line-height: 150%;
    }
    &__desc{
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
      line-height: 21px;
      color: #666487;
    }

  }

  &__exclusive_addon {
    &__title-row {
      display: flex;
      align-items: center;
      margin-bottom: 24px;
    }

    &__title {
      color: #000;
      font-family: Almaden Sans;
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%; /* 33px */
      margin-right: 4px;
    }

    &__subtitle {
      color: #ffffff;
      text-align: center;
      font-family: 'SF Pro', 'Almaden Sans';
      font-size: 14px;
      font-style: normal;
      font-weight: 510;
      line-height: 20px;
      border-radius: 100px;
      background: linear-gradient(81deg, #493AB7 0%, #925DFF 100%);
      padding: 0px 6px;
    }
  }
}

@include media(mobile) {
  .opc-cfgcore {
    &__addon, &__exclusive_addon {
      margin-top: 48px;
      padding-top: 48px;
      border-top: 2px solid #dfe3e8;
    }

    &__addon {
      margin-bottom: 24px;
      &__title {
        font-size: 22px;
      }
      &__desc{
        font-size: 14px;
        line-height: 21px;
      }

    }

    &__exclusive_addon {
      &__title-row {
        margin-bottom: 24px;
      }

      &__title {
        font-size: 22px;
        margin-right: 4px;
      }

      &__subtitle {
        font-size: 14px;
        line-height: 20px;
        border-radius: 100px;
        padding: 0px 6px;
      }
    }
  }
}
</style>
